import React from 'react';
import { ColDef } from 'ag-grid-community';
import { JobApplication } from '../../types/jobs';
import { BtnDefault } from '../common/Buttons';
import { calculateAge } from '../../utils';
import toast from 'react-hot-toast';

interface JobApplicationWithActions extends JobApplication {
  actions?: any;
}

interface JobApplicationColumnParams {
  onView: (data: JobApplicationWithActions) => void;
  onBoard: (data: JobApplicationWithActions) => void;
}

const JobActionsCell: React.FC<{
  data: JobApplicationWithActions;
  onView: (data: JobApplicationWithActions) => void;
  onBoard: (data: JobApplicationWithActions) => void;
}> = ({ data, onView, onBoard }) => {
  return (
    <div className="flex gap-2 p-1">
      {data?.status !== 'selected' && (
        <BtnDefault onClick={() => onView(data)}>View</BtnDefault>
      )}
      <BtnDefault onClick={() => onBoard(data)}>Onboard</BtnDefault>
    </div>
  );
};

// Function to dynamically generate metadata columns based on actual data
export const generateJobApplicationExportColumns = (
  data: JobApplicationWithActions[]
): ColDef<JobApplicationWithActions>[] => {
  const metadataFields = new Set<string>();

  // Collect all unique metadata field names from the data
  data.forEach((item) => {
    if (item.metadata && typeof item.metadata === 'object') {
      Object.keys(item.metadata).forEach((key) => {
        metadataFields.add(key);
      });
    }
  });

  const displayHeaderName = (field: string) =>
    field
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  // Generate column definitions for each metadata field
  return Array.from(metadataFields).map((fieldName) => ({
    field: `metadata.${fieldName}`,
    headerName: displayHeaderName(fieldName),
    hide: true,
    valueGetter: (params: any) => {
      const value = params.data?.metadata?.[fieldName];
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value || '';
    },
  }));
};

export const useJobApplicationColumns = ({
  onView,
  onBoard,
}: JobApplicationColumnParams): ColDef<JobApplicationWithActions>[] => {
  return [
    ...jobApplicationColumnsDefs, // Visible columns
    ...hiddenExportColumns, // Include hidden columns for export
    {
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      filter: false,
      minWidth: 120,
      suppressSizeToFit: true,
      pinned: 'right',
      lockPinned: true,
      cellRenderer: (params: any) => (
        <JobActionsCell data={params.data} onView={onView} onBoard={onBoard} />
      ),
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
      },
    },
  ];
};

const renderAssetLink = (label: string) => (params: any) => {
  const key = params.value;

  if (!key || key.startsWith('http')) {
    return key ? (
      <a
        href={key}
        target="_blank"
        rel="noopener noreferrer"
        className="underline"
      >
        {label}
      </a>
    ) : (
      ''
    );
  }

  // Fetch signed URL when the link is clicked
  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      const { getAssets } = await import('../../api/queries');
      const data = await getAssets(key);
      if (data?.success && data?.asset) {
        const url = data?.asset[0]?.url;
        window.open(url, '_blank');
      } else {
        toast.error('Error fetching asset.');
      }
    } catch {
      toast.error('Failed to fetch signed URL');
    }
  };

  return (
    <button onClick={handleClick} className="underline">
      {label}
    </button>
  );
};

// Hidden columns for export - all additional fields from the actual database document
const hiddenExportColumns: ColDef<JobApplicationWithActions>[] = [
  {
    field: 'jobid',
    headerName: 'Job ID',
    hide: true,
  },
  {
    field: 'personalInfo.familyMembers',
    headerName: 'Family Members',
    hide: true,
  },
  {
    field: 'personalInfo.dependents',
    headerName: 'Dependents',
    hide: true,
  },
  {
    field: 'contactInfo.address.line',
    headerName: 'Address Line',
    hide: true,
  },
  {
    field: 'contactInfo.address.city',
    headerName: 'City',
    hide: true,
  },
  {
    field: 'contactInfo.address.state',
    headerName: 'State',
    hide: true,
  },
  {
    field: 'contactInfo.address.zip',
    headerName: 'ZIP Code',
    hide: true,
  },
  {
    field: 'contactInfo.wa.code',
    headerName: 'Country Code',
    hide: true,
  },
  {
    field: 'contactInfo.wa.countryEnum',
    headerName: 'Country Enum',
    hide: true,
  },
  {
    field: 'workExperience.pastEmployers',
    headerName: 'Past Employers',
    hide: true,
    valueGetter: (params: any) => {
      const employers = params.data?.workExperience?.pastEmployers;
      if (!employers || !Array.isArray(employers) || employers.length === 0)
        return '';
      return employers
        .map(
          (emp: any) =>
            `${emp.employerName || ''} (${emp.startMonth || ''}/${emp.startYear || ''} - ${emp.endMonth || ''}/${emp.endYear || ''})`
        )
        .join('; ');
    },
  },
  {
    field: 'workExperience.resumes',
    headerName: 'All Resumes',
    hide: true,
    valueGetter: (params: any) => {
      return Array.isArray(params.data?.workExperience?.resumes)
        ? params.data.workExperience.resumes.join(', ')
        : '';
    },
  },
  {
    field: 'questionnaire.applicationVideos',
    headerName: 'All Application Videos',
    hide: true,
    valueGetter: (params: any) => {
      return Array.isArray(params.data?.questionnaire?.applicationVideos)
        ? params.data.questionnaire.applicationVideos.join(', ')
        : '';
    },
  },
  {
    field: 'shopifyListingCategory',
    headerName: 'Shopify Categories',
    hide: true,
    valueGetter: (params: any) => {
      return Array.isArray(params.data?.shopifyListingCategory)
        ? params.data.shopifyListingCategory.join(', ')
        : '';
    },
  },
  {
    field: 'relevantIndustries',
    headerName: 'Relevant Industries',
    hide: true,
    valueGetter: (params: any) => {
      return Array.isArray(params.data?.relevantIndustries)
        ? params.data.relevantIndustries.join(', ')
        : '';
    },
  },
  {
    field: 'source',
    headerName: 'Source',
    hide: true,
  },
  {
    field: 'referral',
    headerName: 'Referral',
    hide: true,
  },
  {
    field: 'interviewNotes',
    headerName: 'Interview Notes',
    hide: true,
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    hide: true,
    valueGetter: (params: any) => params?.data?.updatedAt,
    valueFormatter: (params: any) =>
      params.value ? new Date(params.value).toString() : '',
  },
];

const jobApplicationColumnsDefs: ColDef<JobApplicationWithActions>[] = [
  {
    field: 'personalInfo.firstName',
    headerName: 'Full Name',
    valueGetter: (params) => {
      const firstName = params.data?.personalInfo?.firstName || '';
      const middleName = params.data?.personalInfo?.middleName || '';
      const lastName = params.data?.personalInfo?.lastName || '';
      return `${firstName} ${middleName} ${lastName}`.trim();
    },
    minWidth: 200,
    suppressSizeToFit: true,
  },
  {
    field: 'personalInfo.profilePic',
    headerName: 'Profile Picture',
    sortable: true,
    filter: true,
    minWidth: 120,
    suppressSizeToFit: true,
    cellRenderer: renderAssetLink('View Picture'),
  },
  {
    field: 'workExperience.resume',
    headerName: 'Resume',
    sortable: true,
    filter: true,
    cellRenderer: renderAssetLink('View Resume'),
    minWidth: 120,
    suppressSizeToFit: true,
  },
  {
    field: 'questionnaire.applicationVideo',
    headerName: 'Application Video',
    sortable: true,
    filter: true,
    cellRenderer: renderAssetLink('View Video'),
    minWidth: 150,
    suppressSizeToFit: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    sortable: true,
    filter: true,
    minWidth: 200,
    suppressSizeToFit: true,
    cellRenderer: (params: any) => (
      <a href={`mailto:${params.value}`}>{params.value}</a>
    ),
  },
  {
    field: 'contactInfo.wa.number',
    headerName: 'Whatsapp Number',
    valueGetter: (params: any) => {
      return params.data?.contactInfo?.wa?.number;
    },
    sortable: true,
    filter: true,
    minWidth: 150,
    suppressSizeToFit: true,
  },
  {
    field: 'testlifyScore',
    headerName: 'Testlify Score',
    sortable: true,
    filter: true,
    minWidth: 120,
    suppressSizeToFit: true,
    editable: true,
  },
  {
    field: 'screeningStatus',
    headerName: 'Screening Status',
    sortable: true,
    filter: true,
    minWidth: 120,
    cellStyle: (params: any) => {
      return {
        color: params.value === 'pass' ? 'green' : 'red',
      };
    },
    suppressSizeToFit: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    filter: true,
    editable: true,
    singleClickEdit: true,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: [
        'Rejected',
        'InterviewPending',
        'ToBeActioned',
        'NoShow',
        'Candidate-Not-Interested',
        'Hold - Prov Selected',
        'ClientSelected',
        'KimSelected',
        'Resigned',
        'SeparatedByOps',
      ],
    },
    cellRenderer: (params: any) => {
      return (
        <div
          style={{
            borderBottom: '1px solid #e5e7eb',
            padding: '0px 6px',
            borderRadius: '4px',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span style={{ flex: 1 }}>{params.value}</span>
          <span style={{ marginLeft: 6 }}>▾</span>
        </div>
      );
    },
    minWidth: 150,
    suppressSizeToFit: true,
  },
  {
    field: 'personalInfo.dob',
    headerName: 'Age',
    valueGetter: (params) => {
      return params.data?.personalInfo.dob
        ? calculateAge(params.data.personalInfo.dob)
        : '';
    },
    sortable: true,
    filter: true,
    minWidth: 150,
    cellStyle: (params: any) => {
      return {
        color: params.value === 'Minor' ? 'red' : 'green',
      };
    },
    suppressSizeToFit: true,
  },

  {
    field: 'questionnaire.languages',
    headerName: 'Languages',
    valueGetter: (params: any) => {
      return (params.data?.questionnaire?.languages ?? [])
        .map((lang: any) => `${lang.language} (${lang.fluency.join(', ')})`)
        .join('; ');
    },
    sortable: true,
    filter: true,
    minWidth: 200,
    suppressSizeToFit: true,
  },
  {
    field: 'personalInfo.highestEducation',
    headerName: 'Highest Education',
    sortable: true,
    filter: true,
    minWidth: 120,
    suppressSizeToFit: true,
  },
  {
    field: 'createdAt',
    headerName: 'Applied On',
    cellDataType: 'dateTime',
    valueGetter: (params: any) => params?.data?.createdAt, // keep it as raw ISO string
    comparator: (a: string, b: string) => {
      return new Date(a).getTime() - new Date(b).getTime(); // compare timestamps
    },
    valueFormatter: (params: any) => new Date(params.value).toString(),
    filter: 'agDateColumnFilter',
    sortable: true,
    minWidth: 150,
    suppressSizeToFit: true,
  },
  {
    field: 'contactInfo.address.country',
    headerName: 'Country',
    sortable: true,
    filter: true,
    minWidth: 120,
    suppressSizeToFit: true,
  },
  {
    field: 'workExperience.beginWork',
    headerName: 'Can Begin Work',
    sortable: true,
    filter: true,
    minWidth: 150,
    suppressSizeToFit: true,
  },
  {
    field: 'industry',
    headerName: 'Industry',
    sortable: true,
    filter: true,
    minWidth: 150,
    suppressSizeToFit: true,
  },
];
